/* eslint-disable @typescript-eslint/no-explicit-any -- dynamic PostgREST/Supabase rows are shaped at runtime; typed at the boundary. TODO: replace with generated row types. */
/**
 * useContentCalendar — reads every draft with a scheduled_at OR a
 * published_at OR (fallback) an updated_at inside the given month, so the
 * Calendar tab can render one coloured dot per event on its day.
 *
 * Buckets ride alongside so the tab can colour dots without another fetch.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { marketing } from '@/lib/supabase/marketing';
import { useContentBuckets } from './useContentBuckets';

export interface CalendarEvent {
  draft_id: string;
  bucket_slug: string;
  bucket_color: string;
  post_kind: 'single' | 'carousel' | 'story' | 'reel';
  status: 'draft' | 'prompt_ready' | 'image_ready' | 'caption_ready' | 'scheduled' | 'published' | 'failed' | 'archived';
  when: Date;
  when_type: 'scheduled' | 'published' | 'draft';
  hero_r2_key: string | null;
  caption: string | null;
}

interface Args { year: number; month: number; } // month: 0-11

export function useContentCalendar({ year, month }: Args) {
  const bucketsQ = useContentBuckets();
  const bucketMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const b of bucketsQ.data ?? []) m.set(b.slug, b.color_accent ?? '#888');
    return m;
  }, [bucketsQ.data]);

  const monthStart = useMemo(() => new Date(year, month, 1), [year, month]);
  const monthEnd = useMemo(() => new Date(year, month + 1, 1), [year, month]);

  return useQuery<CalendarEvent[]>({
    queryKey: ['content-calendar', year, month, bucketMap.size],
    enabled: bucketsQ.data != null,
    queryFn: async () => {
      // Any draft touched, scheduled, or published in this month.
      const { data, error } = await marketing
        .from('content_draft')
        .select('id,bucket_slug,post_kind,status,scheduled_at,published_at,updated_at,caption,content_draft_slide!inner(slide_index,image_r2_key)')
        .neq('status', 'archived')
        .or(
          `and(scheduled_at.gte.${monthStart.toISOString()},scheduled_at.lt.${monthEnd.toISOString()}),` +
          `and(published_at.gte.${monthStart.toISOString()},published_at.lt.${monthEnd.toISOString()}),` +
          `and(scheduled_at.is.null,published_at.is.null,updated_at.gte.${monthStart.toISOString()},updated_at.lt.${monthEnd.toISOString()})`,
        );
      if (error) throw error;
      const rows = data ?? [];
      const events: CalendarEvent[] = [];
      for (const d of rows as any[]) {
        const heroSlide = (d.content_draft_slide ?? []).find((s: any) => s.slide_index === 0);
        const heroKey = heroSlide?.image_r2_key ?? null;
        const color = bucketMap.get(d.bucket_slug) ?? '#888';
        let when: Date | null = null;
        let whenType: CalendarEvent['when_type'] = 'draft';
        if (d.published_at) { when = new Date(d.published_at); whenType = 'published'; }
        else if (d.scheduled_at) { when = new Date(d.scheduled_at); whenType = 'scheduled'; }
        else if (d.updated_at) { when = new Date(d.updated_at); whenType = 'draft'; }
        if (!when) continue;
        if (when < monthStart || when >= monthEnd) continue;
        events.push({
          draft_id: d.id, bucket_slug: d.bucket_slug, bucket_color: color,
          post_kind: d.post_kind, status: d.status,
          when, when_type: whenType,
          hero_r2_key: heroKey, caption: d.caption,
        });
      }
      return events.sort((a, b) => a.when.getTime() - b.when.getTime());
    },
    staleTime: 30_000,
  });
}
